/**
 * Public `/account/revert#token=…` — the "This wasn't me" link in the notice
 * emailed to the OLD address after a partner sign-in email change (security
 * audit 2026-09-28, migration 086). No sign-in: the token in the link is the
 * authority, and whoever changed the email may be signed in on this browser.
 *
 * The token lives in the URL FRAGMENT, so it never reaches a server log or a
 * Referer. The page only previews on load; reverting is a button the person
 * presses, because mail scanners open links and must not undo anything.
 * Reverting puts the old address back, signs the account out on every device
 * (this browser included), unlinks a Google account linked since the change
 * and revokes AI agent access created since. The link works once, for 14 days.
 */
import { useEffect, useState } from 'react';
import type { EmailReverted, EmailRevertPreview } from '../api/types';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { ErrorNotice, Loading, formatDateTime } from '../ui/kit';
import { ApiError } from '../api/errors';
import { reportTokenFromHash } from './SignupReport';

export function EmailRevert() {
  const { api, status, signOut } = useSession();
  const [token] = useState(() => reportTokenFromHash(window.location.hash));
  const [preview, setPreview] = useState<EmailRevertPreview | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [result, setResult] = useState<EmailReverted | null>(null);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .previewEmailRevert(token)
      .then((p) => !cancelled && setPreview(p))
      .catch((err: unknown) => !cancelled && setPreviewError(err instanceof ApiError ? err : null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  let body: React.ReactNode;
  if (!token) {
    body = (
      <div className="notice bad" role="alert">
        <span>This link is incomplete. Open the “This wasn’t me” link from the email again, or contact support@finderlyfix.com.</span>
      </div>
    );
  } else if (loading) {
    body = <Loading label="Checking the link" />;
  } else if (result) {
    body = (
      <div className="notice ok" role="status" data-testid="revert-done">
        <div className="stack-sm" style={{ gap: 4 }}>
          <strong>Done. Your sign-in email is {result.email} again.</strong>
          <span>We signed the account out on every device. Sign in with a code sent to {result.email}.</span>
          {result.google_unlinked ? <span>We also unlinked a Google account that was linked after the change.</span> : null}
          {result.agent_tokens_revoked > 0 ? (
            <span>
              We revoked {result.agent_tokens_revoked} AI agent {result.agent_tokens_revoked === 1 ? 'connection' : 'connections'}{' '}
              created after the change.
            </span>
          ) : null}
          <span>
            Check your organisation’s settings and team for anything you don’t recognise, and tell support@finderlyfix.com what
            happened.
          </span>
          <span>
            <Link to="/sign-in">Sign in</Link>
          </span>
        </div>
      </div>
    );
  } else if (previewError || !preview) {
    body = <ErrorNotice error={previewError ?? new ApiError({ status: 404, code: 'EMAIL_REVERT_LINK_INVALID' })} />;
  } else if (preview.used) {
    body = (
      <div className="notice info" role="status" data-testid="revert-used">
        <span>
          <strong>This link has already been used.</strong> It works once. If you still can’t sign in with {preview.restores_email},
          contact support@finderlyfix.com.
        </span>
      </div>
    );
  } else {
    body = (
      <div className="stack-sm">
        <p>The sign-in email for a FinderlyFix partner portal account was changed away from your address:</p>
        <dl className="facts" data-testid="revert-facts">
          <dt>Your address</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>{preview.restores_email}</dd>
          <dt>Changed to</dt>
          <dd>{preview.changed_to}</dd>
          <dt>When</dt>
          <dd>{formatDateTime(preview.changed_at)}</dd>
          <dt>Link works until</dt>
          <dd>{formatDateTime(preview.expires_at)}</dd>
        </dl>
        <p>
          If you didn’t make this change, undo it. Your address becomes the sign-in email again, and the account is signed out on
          every device, including whoever made the change. Nothing else is deleted.
        </p>
        <ErrorNotice error={revertError} />
        <div className="row">
          <button
            type="button"
            className="btn btn-danger"
            disabled={reverting}
            onClick={async () => {
              setReverting(true);
              setRevertError(null);
              try {
                const done = await api.revertEmail(token);
                setResult(done);
                // Every session was revoked, this browser's included.
                if (status !== 'signed_out' && status !== 'loading') await signOut().catch(() => undefined);
              } catch (err) {
                setRevertError(err instanceof ApiError ? err : new ApiError({ status: 500, code: 'INTERNAL_ERROR' }));
              } finally {
                setReverting(false);
              }
            }}
          >
            {reverting ? 'Reverting…' : 'This wasn’t me: revert and sign out everywhere'}
          </button>
        </div>
        <p className="small muted">If you did make the change, you don’t need this page. The link stops working after 14 days.</p>
      </div>
    );
  }

  return (
    <main className="shared-dashboard legal-page" id="main">
      <header className="row-between legal-head">
        <Link to="/" className="eyebrow legal-home">
          FinderlyFix Partners
        </Link>
      </header>
      <article className="stack" aria-labelledby="revert-title">
        <h1 id="revert-title">This wasn’t me</h1>
        {body}
      </article>
    </main>
  );
}
